import { defineStore } from 'pinia';
import { ref } from 'vue';
import { firstValueFrom } from 'rxjs';

import { OrderService } from '~/support/services';
import createBus from '~/support/utils/event-bus';

export const useOrdersStore = defineStore('orders', () => {
  const endDate = new Date();
  const startDate = new Date(new Date().setDate(endDate.getDate() - 7));
  const dateRange = ref([startDate, endDate]);
  const rowsPerPage = ref(10);
  const currentPage = ref(1);
  const config = useRuntimeConfig();
  const service = new OrderService(config.public.gatewayBaseUrl);

  const orderCounts = ref(new Map<string, number>());
  const { orders: restSvc } = useGateway();

  const $bus = createBus();

  function setDateRange(range: any) {
    dateRange.value = range;
  }

  function setRowsPerPage(rows: number) {
    rowsPerPage.value = rows;
  }

  function setCurrentPage(page: number) {
    currentPage.value = page;
  }

  async function exportOrders(body: { storeIds: string[]; filter: string; timezone?: string }) {
    return service.exportOrders(body);
  }

  async function loadOrderCounts({ filters }: { filters: string }, force = false): Promise<number> {
    const cacheKey = JSON.stringify({ filters });

    if (!force && orderCounts.value.has(cacheKey)) {
      return orderCounts.value.get(cacheKey)!;
    }

    try {
      const response = await firstValueFrom(restSvc.orders.counts.forStores({ filters }));

      const count = response.data?.totalCount || 0;
      orderCounts.value.set(cacheKey, count);

      return count;
    } catch (error) {
      console.error('Error loading order counts:', error);

      return 0;
    }
  }

  function getOrderBySourceOrderId(email: string, sourceOrderId: string) {
    return service.findByEmailAndSourceOrderId(email, sourceOrderId);
  }

  /** Zeroes rather than throws: an unreadable refund history must not block an approval. */
  async function getRefundedTotals(orderId: string) {
    try {
      const response = await firstValueFrom(restSvc.orders.getOrderRefunds(orderId));

      return response.data?.totals ?? null;
    } catch (error) {
      console.error('Error getting order refunds:', error);

      return null;
    }
  }

  async function getRootOrder(orderId: string, storeId: string) {
    try {
      const response = await firstValueFrom(restSvc.orders.getRootOrder(orderId, storeId));

      return response.data;
    } catch (error) {
      console.error('Error getting root order:', error);

      return null;
    }
  }

  return {
    $bus,
    currentPage,
    dateRange,
    exportOrders,
    getOrderBySourceOrderId,
    getRefundedTotals,
    getRootOrder,
    loadOrderCounts,
    orderCounts,
    rowsPerPage,
    setCurrentPage,
    setDateRange,
    setRowsPerPage,
  };
});
